import React, { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import { Button, Input, Row, Col, Label, FormFeedback, Spinner } from 'reactstrap';
import { Close as CloseIcon, ArrowUpward as ArrowUpIcon, ArrowDownward as ArrowDownIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useAppDispatch } from 'app/config/store';
import { uploadQuotationDocuments, deleteAllFilesForQuotation } from 'app/entities/file/file.reducer';
import { convertFileToBase64 } from 'app/shared/util/file-utils';
import { Translate, translate } from 'react-jhipster';
import axios from 'axios';
import { isEditable } from 'app/shared/util/quotation-utils';
import { IQuotation } from 'app/shared/model/quotation.model';

type FileEntry = {
  id: number;
  fileType: string;
  file?: File;
  fileName: string;
  order: number;
  existingFile?: boolean;
};

type DocUploadDialogProps = {
  isOpen: boolean;
  toggle: () => void;
  initialFiles?: FileEntry[];
  initialTransactionRefNo?: string;
  quotationId: number | string;
  onSuccess?: () => void;
  quotationEntity: IQuotation;
};

export const DocUploadDialog = ({
  isOpen,
  toggle,
  initialFiles = [],
  initialTransactionRefNo = '',
  quotationId,
  onSuccess = () => {},
  quotationEntity,
}: DocUploadDialogProps) => {
  const dispatch = useAppDispatch();
  const [files, setFiles] = useState<FileEntry[]>([]);
  const [fileType, setFileType] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [transactionRefNo, setTransactionRefNo] = useState(initialTransactionRefNo || '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState({
    fileType: false,
    file: false,
    transactionRefNo: false,
  });
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Use a ref to track whether we've already processed the initialFiles
  const initializedRef = useRef(false);

  const isMounted = useRef(true);
  const maxEditableDays = quotationEntity?.bank?.quotationMaxEditableDays;
  const createdDate = quotationEntity?.createdDate ? new Date(quotationEntity.createdDate) : null;
  const currentDate = new Date();

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Initialize data from props when dialog opens and fetch file data for existing files
  useEffect(() => {
    // Only run this effect when the dialog opens and we haven't initialized
    if (isOpen && !initializedRef.current) {
      // Set transaction reference number if provided
      if (initialTransactionRefNo) {
        setTransactionRefNo(initialTransactionRefNo);
      }

      // Set files if provided and fetch file data for existing files
      if (initialFiles && initialFiles.length > 0) {
        const fetchFileData = async () => {
          const filesWithData = await Promise.all(
            initialFiles.map(async file => {
              // Fetch file data if it's an existing file
              if (file.existingFile) {
                try {
                  // This would require an API endpoint to fetch file data
                  const response = await axios.get(`/api/files/${file.id}`, {
                    responseType: 'blob',
                  });

                  const fileData = response.data;
                  const actualFile = new File([fileData], file.fileName, { type: 'application/pdf' });

                  return {
                    ...file,
                    file: actualFile,
                  };
                } catch (fetchError) {
                  console.error(`Failed to fetch file data for ${file.fileName}:`, fetchError);
                  return file;
                }
              }
              return file;
            })
          );

          setFiles(filesWithData);
        };

        fetchFileData();
      }

      // Mark as initialized
      initializedRef.current = true;
    } else if (!isOpen) {
      // Reset the initialized flag when dialog closes
      initializedRef.current = false;
    }
  }, [isOpen, initialFiles, initialTransactionRefNo, quotationId]);

  // Separate effect for resetting the form when dialog closes
  useEffect(() => {
    if (!isOpen) {
      // Reset form when dialog closes
      setFileType('');
      setSelectedFile(null);
      setFiles([]);
      if (!initialTransactionRefNo) {
        setTransactionRefNo('');
      }
      setError({ fileType: false, file: false, transactionRefNo: false });
      setIsSubmitting(false);
    }
  }, [isOpen, initialTransactionRefNo]);

  const handleTransactionRefNoChange = e => {
    setTransactionRefNo(e.target.value);
    setError({ ...error, transactionRefNo: false });
  };

  const handleFileTypeChange = e => {
    setFileType(e.target.value);
    setError({ ...error, fileType: false });
  };

  const handleFileChange = e => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
      setError({ ...error, file: false });
    }
  };

  const handleAddFile = () => {
    // Validate inputs
    const newError = {
      fileType: false,
      file: false,
      transactionRefNo: false,
    };

    if (!fileType.trim()) {
      newError.fileType = true;
    }

    if (!selectedFile) {
      newError.file = true;
    }

    // Check if file is PDF
    if (selectedFile && !selectedFile.type.includes('pdf')) {
      newError.file = true;
      toast.error(translate('comexApp.quotation.pdfOnly'));
      return;
    }

    // Check if file type already exists
    const fileTypeExists = files.some(file => file.fileType.toLowerCase() === fileType.toLowerCase());
    if (fileTypeExists) {
      toast.error(translate('comexApp.quotation.fileTypeExists'));
      return;
    }

    if (selectedFile) {
      const fileNameExists = files.some(file => file.fileName.toLowerCase() === selectedFile.name.toLowerCase());
      if (fileNameExists) {
        toast.error(translate('comexApp.quotation.fileNameExists'));
        return;
      }
    }

    if (newError.fileType || newError.file) {
      setError(newError);
      return;
    }

    // Add the file to the list
    setFiles([
      ...files,
      {
        id: Date.now(),
        fileType,
        file: selectedFile,
        fileName: selectedFile.name,
        order: files.length, // Set the order to the current length
      },
    ]);

    // Reset form
    setFileType('');
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveFile = id => {
    setFiles(files.filter(file => file.id !== id));
  };

  const moveFile = (index, direction) => {
    if ((direction === 'up' && index === 0) || (direction === 'down' && index === files.length - 1)) {
      return;
    }

    const newFiles = [...files];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;

    [newFiles[index], newFiles[targetIndex]] = [newFiles[targetIndex], newFiles[index]];

    // Update order property for all files
    newFiles.forEach((file, idx) => {
      file.order = idx;
    });

    setFiles(newFiles);
  };

  const handleSubmit = async () => {
    // Validate transaction reference number
    const newError = { ...error };

    if (!transactionRefNo.trim()) {
      newError.transactionRefNo = true;
      setError(newError);
      return;
    }

    if (files.length === 0) {
      toast.error(translate('comexApp.quotation.addFileFirst'));
      return;
    }

    setIsSubmitting(true);

    try {
      // First, delete all existing files to replace them with new/edited ones
      if (initialFiles && initialFiles.length > 0) {
        await dispatch(deleteAllFilesForQuotation(quotationId)).unwrap();
      }

      // Update order property for all files (ensure correct ordering)
      const orderedFiles = files.map((file, index) => ({
        ...file,
        order: index,
      }));

      // Prepare files for upload
      const filesToUpload = await Promise.all(
        orderedFiles.map(async fileEntry => {
          if (fileEntry.file) {
            const base64Data = await convertFileToBase64(fileEntry.file);
            return {
              fileType: fileEntry.fileType,
              fileName: fileEntry.fileName,
              file: base64Data,
              order: fileEntry.order,
            };
          }
          return null;
        })
      );

      // Filter out null entries
      const validFilesToUpload = filesToUpload.filter(file => file !== null);

      // Only proceed if we have files to upload
      if (validFilesToUpload.length > 0) {
        // Prepare upload data
        const uploadData = {
          transactionRefNo,
          files: validFilesToUpload,
        };

        // Call the API
        await dispatch(uploadQuotationDocuments({ id: quotationId, documents: uploadData })).unwrap();
      }

      toast.success(translate('comexApp.quotation.uploadSuccess'));

      // Close the dialog
      toggle();

      // Call onSuccess callback
      onSuccess();
    } catch (uploadError) {
      console.error('Error updating files:', uploadError);
      toast.error(translate('comexApp.quotation.uploadFailed'));
    } finally {
      if (isMounted.current) {
        setIsSubmitting(false);
      }
    }
  };

  const handleCancel = () => {
    toggle();
  };

  return (
    <Dialog open={isOpen} onClose={handleCancel} fullWidth maxWidth="md" aria-labelledby="file-upload-dialog-title">
      <DialogTitle id="file-upload-dialog-title">
        {initialFiles && initialFiles.length > 0 ? (
          <Translate contentKey="comexApp.quotation.editDocuments">Edit Documents</Translate>
        ) : (
          <Translate contentKey="comexApp.quotation.uploadFiles">Upload Files</Translate>
        )}
        <IconButton
          aria-label="close"
          onClick={handleCancel}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {/* Transaction Reference Number Field */}
        <Row className="mb-4 mt-2">
          <Col md={12}>
            <Label for="transactionRefNo" className="form-label">
              <Translate contentKey="comexApp.quotation.transactionRefNo">Transaction Reference Number</Translate>{' '}
              <span className="text-danger">*</span>
            </Label>
            <Input
              id="transactionRefNo"
              type="text"
              value={transactionRefNo}
              onChange={handleTransactionRefNoChange}
              invalid={error.transactionRefNo}
              placeholder={translate('comexApp.quotation.enterTransactionRefNo')}
              disabled={!isEditable(createdDate, maxEditableDays, currentDate)}
            />
            {error.transactionRefNo && (
              <FormFeedback>
                <Translate contentKey="comexApp.quotation.transactionRefNoRequired">Transaction reference number is required</Translate>
              </FormFeedback>
            )}
          </Col>
        </Row>

        {/* File Upload Section */}
        <Row className="mb-4">
          <Col md={4}>
            <Label for="fileType" className="form-label">
              <Translate contentKey="comexApp.quotation.fileType">File Type</Translate> <span className="text-danger">*</span>
            </Label>
            <Input
              id="fileType"
              type="text"
              value={fileType}
              onChange={handleFileTypeChange}
              invalid={error.fileType}
              placeholder={translate('comexApp.quotation.enterFileType')}
            />
            {error.fileType && (
              <FormFeedback>
                <Translate contentKey="comexApp.quotation.fileTypeRequired">File type is required</Translate>
              </FormFeedback>
            )}
          </Col>

          <Col md={6}>
            <Label for="fileUpload" className="form-label">
              <Translate contentKey="comexApp.quotation.uploadPdf">Upload PDF</Translate> <span className="text-danger">*</span>
            </Label>
            <Input id="fileUpload" type="file" accept=".pdf" onChange={handleFileChange} invalid={error.file} innerRef={fileInputRef} />
            {error.file && (
              <FormFeedback>
                <Translate contentKey="comexApp.quotation.pdfRequired">PDF file is required</Translate>
              </FormFeedback>
            )}
          </Col>

          <Col md={2} className="d-flex align-items-end">
            <Button color="primary" onClick={handleAddFile} className="w-100">
              <Translate contentKey="comexApp.quotation.addFile">Add File</Translate>
            </Button>
          </Col>
        </Row>

        {/* Files Table */}
        {files.length > 0 && (
          <TableContainer component={Paper} className="mt-4">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell width="5%">#</TableCell>
                  <TableCell width="25%">
                    <Translate contentKey="comexApp.quotation.fileType">File Type</Translate>
                  </TableCell>
                  <TableCell width="45%">
                    <Translate contentKey="comexApp.quotation.fileName">File Name</Translate>
                  </TableCell>
                  <TableCell width="25%" align="center">
                    <Translate contentKey="comexApp.quotation.actions">Actions</Translate>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {files.map((file, index) => (
                  <TableRow key={file.id}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>{file.fileType}</TableCell>
                    <TableCell>{file.fileName}</TableCell>
                    <TableCell align="center">
                      <IconButton size="small" onClick={() => moveFile(index, 'up')} disabled={index === 0} color="primary">
                        <ArrowUpIcon />
                      </IconButton>

                      <IconButton
                        size="small"
                        onClick={() => moveFile(index, 'down')}
                        disabled={index === files.length - 1}
                        color="primary"
                      >
                        <ArrowDownIcon />
                      </IconButton>

                      <IconButton size="small" onClick={() => handleRemoveFile(file.id)} color="error">
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>

      <DialogActions className="p-3">
        <Button color="secondary" onClick={handleCancel}>
          <Translate contentKey="comexApp.quotation.cancel">Cancel</Translate>
        </Button>
        <Button color="primary" onClick={handleSubmit} disabled={isSubmitting}>
          {isSubmitting ? (
            <>
              <Spinner size="sm" className="me-1" />
              <Translate contentKey="comexApp.quotation.uploading">Uploading...</Translate>
            </>
          ) : (
            <Translate contentKey="comexApp.quotation.submit">Submit</Translate>
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
